import { useState, useEffect, useCallback, useMemo } from 'react';
import { url as apiUrl, url } from '../../../api';
import { apiClient } from '../../../api/client';
import { coffeeChainPaths, COFFEE_API_BASE } from '../../coffee_card_web/api/paths';
import { getAuthToken } from '../../../utils/authTokens';
import { useLocalCache, UseLocalCacheResult } from '../../coffee_shops/utils/swrCache';

export interface ChainShop {
    _id: string;
    name: string;
    address?: string;
    city?: string;
    country?: string;
    coordinates?: { lat: number; lng: number };
    place_id?: string;
}

export interface ChainPublicShop {
    shopId: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
    avgRating: number;
    ratingCount: number;
    distanceMeters?: number;
}

export interface UseChainPublicShopsOptions {
    lat?: number | null;
    lng?: number | null;
}

const publicShopsUrl = apiUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

/**
 * Public list of approved shops for a chain. When `lat`/`lng` are provided,
 * the backend returns `distanceMeters` per shop. Cache key includes the
 * rounded origin so a different geolocation hit re-fetches.
 */
export function useChainPublicShops(
    chainId: string | undefined,
    { lat, lng }: UseChainPublicShopsOptions = {}
): UseLocalCacheResult<ChainPublicShop[]> {
    const safeLat = typeof lat === 'number' && Number.isFinite(lat) ? lat : null;
    const safeLng = typeof lng === 'number' && Number.isFinite(lng) ? lng : null;
    const roundedLat = safeLat != null ? Math.round(safeLat * 1000) / 1000 : null;
    const roundedLng = safeLng != null ? Math.round(safeLng * 1000) / 1000 : null;
    const key = chainId
        ? roundedLat != null && roundedLng != null
            ? `coffee_chain_public_shops_${chainId}_${roundedLat}_${roundedLng}`
            : `coffee_chain_public_shops_${chainId}`
        : null;

    const fetcher = useMemo(
        () => async () => {
            const params = safeLat != null && safeLng != null
                ? { lat: safeLat, lng: safeLng }
                : undefined;
            const response = await apiClient.get<ChainPublicShop[]>(
                `${publicShopsUrl}${COFFEE_API_BASE}/chains/${chainId}/public-shops`,
                { params }
            );
            return Array.isArray(response.data) ? response.data : [];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [chainId, safeLat, safeLng]
    );

    return useLocalCache<ChainPublicShop[]>(key, 5 * 60 * 1000, fetcher);
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

export function useChainShops(chainId: string | null, page = 1, limit = 20) {
    const [shops, setShops] = useState<ChainShop[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(!!chainId);
    const [error, setError] = useState<string | null>(null);

    const fetchShops = useCallback(async () => {
        if (!chainId) {
            setShops([]);
            setTotal(0);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get(`${url}${coffeeChainPaths.shops(chainId)}`, {
                ...authHeaders(),
                params: { page, limit }
            });
            setShops(res.data?.shops || []);
            setTotal(res.data?.total ?? 0);
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Failed to fetch shops');
            setShops([]);
        } finally {
            setLoading(false);
        }
    }, [chainId, page, limit]);

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    return { shops, total, loading, error, refetch: fetchShops };
}
